/**
 * Unit handling for *display*: splitting a user-typed unit so a number can be
 * given exactly one SI prefix.
 *
 * The physics lives in `engine/units/` and is not duplicated here. This module
 * needs something the engine deliberately does not produce — the base unit's
 * *spelling*, because `5.000 V` has to print the letter V — so it resolves the
 * symbol against the engine's tables rather than keeping its own list. Two
 * tables would drift, and a formatter that thinks `mV` is millivolts while the
 * dimensional checker thinks it is metre-volts is worse than either alone.
 */

import { resolveSymbol } from '../engine/units/registry.ts';

export interface ParsedUnit {
  /** Multiply a value in this unit by `scale` to get the base-unit value. */
  scale: number;
  /**
   * The prefix-free unit in canonical spelling — `V` for `mV`, and `Ω` for
   * `ohm`, `Ohm` or `OHM`. Display is normalised deliberately: two variables
   * holding the same quantity should not read as two different units because
   * one of them was typed on a keyboard without a Greek layout.
   */
  base: string;
}

/**
 * Split a unit into its prefix scale and base symbol, or return null when the
 * unit is not a single prefixable symbol.
 *
 * Compound units (`m/s`, `1/°C`) return null: they are perfectly valid and the
 * engine understands them, but there is no single place to hang a prefix, so
 * the formatter prints them verbatim rather than inventing `k(m/s)`.
 *
 * The whole string is tested as a base *before* any prefix is stripped, which
 * is what keeps `Pa` from parsing as pico-a and `mol` as milli-ol.
 */
/**
 * Known to the engine, but never given a prefix for display.
 *
 * Degrees Celsius is a genuine temperature dimensionally — the checker needs
 * that — but it is an *offset* scale, and a prefix on an offset scale means
 * nothing. `1.000 m°C` is not a millidegree, it is a category error. Printed
 * verbatim instead.
 */
const OFFSET_SCALES = new Set(['°C']);

/**
 * True for units whose zero is arbitrary rather than an absence of the
 * quantity.
 *
 * Two things follow, and both are correctness rather than taste. A prefix on an
 * offset scale is a category error (`1.000 m°C` is not a millidegree), and a
 * percentage of a value on one is meaningless — 22.5 °C ± 62.5 °C is not a
 * "280% tolerance", it is an artifact of where the Celsius zero happens to sit.
 */
export function isOffsetScale(unit: string | undefined): boolean {
  const u = unit?.trim();
  if (!u) return false;
  const resolved = resolveSymbol(u);
  return OFFSET_SCALES.has(resolved ? resolved.canonical : u);
}

export function parseUnit(unit: string | undefined): ParsedUnit | null {
  const u = unit?.trim();
  if (!u) return null;

  const resolved = resolveSymbol(u);
  if (!resolved) return null;

  // Tested against the canonical symbol, so `degC` is caught alongside `°C`.
  if (OFFSET_SCALES.has(resolved.canonical)) return null;

  // `prefixScale`, not `scale`: this is relative to `base`, which is what gets
  // printed next to the number. See the note on ResolvedSymbol.
  return { scale: resolved.prefixScale, base: resolved.canonical };
}

/**
 * Prefixes used when *writing* a unit: engineering notation, so exponents are
 * multiples of three and every one of them is a prefix an engineer reads
 * without conversion. Narrower than the set the engine accepts when reading —
 * `cm` is a unit people type, `cV` is not one anybody wants to be shown.
 */
const EMIT_PREFIXES: [number, string][] = [
  [1e12, 'T'],
  [1e9, 'G'],
  [1e6, 'M'],
  [1e3, 'k'],
  [1, ''],
  [1e-3, 'm'],
  [1e-6, 'µ'],
  [1e-9, 'n'],
  [1e-12, 'p'],
  [1e-15, 'f'],
];

/**
 * Pick the engineering prefix for a magnitude expressed in base units.
 * Returns the divisor and the prefix to append.
 */
export function choosePrefix(magnitude: number): { divisor: number; prefix: string } {
  const abs = Math.abs(magnitude);
  if (!Number.isFinite(abs) || abs === 0) return { divisor: 1, prefix: '' };
  const entry =
    EMIT_PREFIXES.find(([scale]) => abs >= scale) ?? EMIT_PREFIXES[EMIT_PREFIXES.length - 1];
  return { divisor: entry[0], prefix: entry[1] };
}
